import { useState } from "react";
import { Mail, Briefcase, MailCheck } from "lucide-react";
import LegalPage, { SUPPORT_EMAIL } from "@/components/LegalPage";
import { inputClass } from "@/components/AuthLayout";
import { apiFetch } from "@/api";

const EMAIL_RE = /^\S+@\S+\.\S+$/;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError("Please enter your name");
    if (!EMAIL_RE.test(form.email.trim()))
      return setError("Please enter a valid email address");
    if (!form.subject.trim()) return setError("Please enter a subject");
    if (form.message.trim().length < 10)
      return setError("Please write a message of at least 10 characters");
    setError("");
    setSending(true);
    try {
      await apiFetch("/contact", {
        method: "POST",
        body: {
          name: form.name.trim(),
          email: form.email.trim(),
          subject: form.subject.trim(),
          message: form.message.trim(),
        },
      });
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const sections = [
    {
      title: "General Support",
      body: (
        <>
          <div className="mb-4 inline-flex rounded-2xl bg-[#16A34A]/10 p-3 text-[#16A34A]">
            <Mail className="h-5 w-5" />
          </div>
          <p>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              data-testid="contact-support-email"
              className="font-bold text-[#16A34A] hover:underline"
            >
              {SUPPORT_EMAIL}
            </a>
          </p>
          <p>Use this for:</p>
          <ul className="list-disc space-y-1.5 pl-5">
            <li>Account issues</li>
            <li>Subscription questions</li>
            <li>Refund requests</li>
            <li>Technical problems</li>
            <li>General queries &amp; feedback</li>
          </ul>
        </>
      ),
    },
    {
      title: "Business / Partnership Enquiries",
      body: (
        <>
          <div className="mb-4 inline-flex rounded-2xl bg-[#16A34A]/10 p-3 text-[#16A34A]">
            <Briefcase className="h-5 w-5" />
          </div>
          <p>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              data-testid="contact-business-email"
              className="font-bold text-[#16A34A] hover:underline"
            >
              {SUPPORT_EMAIL}
            </a>
          </p>
          <p>
            For partnerships, schools, and business conversations, write to the
            same address with "Business" in your subject line.
          </p>
        </>
      ),
    },
    {
      title: "Send Us a Message",
      body: (
        <>
          <p>
            We aim to respond to support requests as soon as reasonably
            possible.
          </p>
          {sent ? (
            <div data-testid="contact-sent" className="rounded-2xl border border-[#E3ECE6] bg-white p-8 text-center">
              <div className="mx-auto mb-4 inline-flex rounded-2xl bg-[#16A34A]/10 p-3.5 text-[#16A34A]">
                <MailCheck className="h-7 w-7" />
              </div>
              <p className="text-sm font-semibold leading-relaxed text-[#17201A]/80">
                Thanks, {form.name.split(" ")[0]}! Your message has been sent
                to our support team.
              </p>
            </div>
          ) : (
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="space-y-4 rounded-2xl border border-[#E3ECE6] bg-white p-6 sm:p-8"
              noValidate
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-bold text-[#17201A]">
                    Name
                  </label>
                  <input
                    data-testid="contact-name-input"
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-bold text-[#17201A]">
                    Email
                  </label>
                  <input
                    data-testid="contact-email-input"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-bold text-[#17201A]">
                  Subject
                </label>
                <input
                  data-testid="contact-subject-input"
                  type="text"
                  value={form.subject}
                  onChange={set("subject")}
                  placeholder="What is this about?"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-bold text-[#17201A]">
                  Message
                </label>
                <textarea
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us how we can help..."
                  rows={5}
                  className={`${inputClass} resize-none`}
                />
              </div>
              {error && (
                <p
                  data-testid="contact-form-error"
                  className="text-xs font-semibold text-red-500"
                >
                  {error}
                </p>
              )}
              <button
                type="submit"
                data-testid="contact-submit-btn"
                data-magnetic="true"
                disabled={sending}
                className="w-full rounded-full bg-[#16A34A] py-4 text-base font-bold text-white shadow-sm transition-[background-color,transform] duration-200 hover:bg-[#15803D] disabled:opacity-60 sm:w-auto sm:px-10"
              >
                {sending ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </>
      ),
    },
  ];

  return (
    <LegalPage
      testid="contact-page"
      title="Contact Abhyas"
      intro="Have a question, need help, or want to share feedback? We're here to help."
      sections={sections}
    />
  );
};

export default Contact;
